import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, LogOut, ChevronRight, Search, ChevronDown } from 'lucide-react';
import { NavData, SecondaryNavData } from '../Constant';
import SessionsListSection from './SessionsListSection';
import feedaLogo from '../../assets/feedaia-logo.svg';
import logoIcon from '../../assets/logo-icon.svg';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import { useAgents } from '../../hooks';
import { cn } from '../../lib/utils';
import { DEFAULT_COPILOT_ID, DEFAULT_COPILOT_NAME } from '../../constants/defaultCopilot';

const SideBar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [agentData, setAgentData] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState({
    label: 'All',
    value: '',
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const [filterPos, setFilterPos] = useState(null);
  const filterBtnRef = useRef(null);
  const filterMenuRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const { user, onLogout } = useContextHook(AuthContext, v => ({
    user: v.user,
    onLogout: v.onLogout,
  }));

  const getAllAgentData = useAgents();

  useEffect(() => {
    if (getAllAgentData.isSuccess) {
      setAgentData([
        { agent_name: 'All', id: '' },
        { agent_name: DEFAULT_COPILOT_NAME, id: DEFAULT_COPILOT_ID },
        ...(getAllAgentData?.data?.agents || []),
      ]);
    }
  }, [getAllAgentData.isSuccess, getAllAgentData?.data?.agents]);

  useEffect(() => {
    if (!collapsed) {
      document.body.classList.add('sidebar-active');
    } else {
      document.body.classList.remove('sidebar-active');
    }
  }, [collapsed]);

  useEffect(() => {
    const handleClickOutside = event => {
      const target = event.target;
      if (filterBtnRef.current?.contains(target) || filterMenuRef.current?.contains(target)) return;
      setFilterOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleFilter = () => {
    if (filterOpen) {
      setFilterOpen(false);
      return;
    }
    const rect = filterBtnRef.current?.getBoundingClientRect();
    if (rect) {
      setFilterPos({ top: rect.bottom + 4, left: rect.right + 8 });
    }
    setFilterOpen(true);
  };

  const filteredAgentData = agentData?.filter(agent =>
    (agent?.agent_name || '').toLowerCase().includes((filterQuery || '').toLowerCase()),
  );

  const isActive = item => {
    const path = `/${item.path}`;
    if (item.exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const initial = user?.first_name?.trim()?.[0]?.toUpperCase() || 'U';
  const displayName = user?.first_name || 'User';

  const linkClass = (active, justifyCenter) =>
    cn(
      'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors relative',
      justifyCenter && 'justify-center',
      active ? 'bg-white text-ink font-semibold' : 'text-ink/75 hover:text-ink hover:bg-white/60',
    );

  return (
    <aside
      className={cn(
        'flex-shrink-0 bg-paper-2 border-r border-shell-border flex flex-col h-screen sticky top-0 transition-all duration-200 z-50',
        collapsed ? 'w-16' : 'w-64',
      )}>
      <div
        className={cn(
          'h-16 flex items-center border-b border-shell-border flex-shrink-0',
          collapsed ? 'px-3 justify-center gap-1' : 'px-4 justify-between',
        )}>
        <Link to="/dashboard" className="inline-flex items-center min-w-0" aria-label="Abode Labs">
          <img
            src={collapsed ? logoIcon : feedaLogo}
            alt="Abode Labs"
            className={collapsed ? 'w-7 h-7 object-contain' : 'h-8 w-auto max-w-[148px] object-contain'}
          />
        </Link>
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="p-1.5 rounded-md text-mute hover:text-ink hover:bg-white/70"
          title={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-3">
        <ul className="space-y-0.5 px-2">
          {NavData.map(item => {
            const active = isActive(item);
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <NavLink
                  to={`/${item.path}`}
                  title={collapsed ? item.name : undefined}
                  className={linkClass(active, collapsed)}>
                  {active ? (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-space rounded-r" />
                  ) : null}
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {!collapsed && <span className="truncate">{item.name}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>

        {!collapsed && (
          <div className="mt-6 px-2">
            <div className="flex items-center justify-between mb-2 px-2">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-mute">· Chats</span>
              <button
                ref={filterBtnRef}
                type="button"
                onClick={toggleFilter}
                className="inline-flex items-center gap-1 text-xs text-mute hover:text-ink bg-transparent border-0 cursor-pointer p-0">
                {selectedAgent?.label || 'All'}
                <ChevronDown className="w-3 h-3" />
              </button>
              {filterOpen &&
                createPortal(
                  <div
                    ref={filterMenuRef}
                    style={{ position: 'fixed', top: filterPos?.top, left: filterPos?.left }}
                    className="w-56 rounded-lg border border-shell-border bg-white shadow-lg z-[100] overflow-hidden">
                    <div className="flex items-center gap-2 px-2.5 py-2 border-b border-shell-border">
                      <Search className="w-3.5 h-3.5 text-mute flex-shrink-0" />
                      <input
                        autoFocus
                        value={filterQuery}
                        onChange={e => setFilterQuery(e.target.value)}
                        placeholder="Search agents"
                        className="w-full text-xs text-ink placeholder:text-mute outline-none bg-transparent"
                      />
                    </div>
                    <ul className="max-h-48 overflow-y-auto p-1">
                      {filteredAgentData?.map((data, index) => (
                        <li key={index}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedAgent({ label: data?.agent_name, value: data?.id });
                              setFilterOpen(false);
                              setFilterQuery('');
                            }}
                            className={`w-full text-left px-2.5 py-1.5 text-xs rounded-md transition-colors ${
                              selectedAgent?.value === data?.id && selectedAgent?.label === data?.agent_name
                                ? 'bg-paper text-ink font-semibold'
                                : 'text-ink/75 hover:bg-paper hover:text-ink'
                            }`}>
                            {data?.agent_name}
                          </button>
                        </li>
                      ))}
                      {filteredAgentData?.length === 0 && (
                        <li className="px-2.5 py-2 text-xs text-mute">No agents found</li>
                      )}
                    </ul>
                  </div>,
                  document.body,
                )}
            </div>
            <div className="max-h-[220px] overflow-y-auto overflow-x-hidden px-1">
              <SessionsListSection selectedAgent={selectedAgent} />
            </div>
          </div>
        )}
      </nav>

      <div className="flex-shrink-0 border-t border-shell-border px-2 py-3">
        <ul className="space-y-0.5">
          {SecondaryNavData.map(item => {
            const active = isActive(item);
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <NavLink
                  to={`/${item.path}`}
                  title={collapsed ? item.name : undefined}
                  className={linkClass(active, collapsed)}>
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {!collapsed && item.name}
                </NavLink>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              onClick={onLogout}
              title={collapsed ? 'Logout' : undefined}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-ink/75 hover:text-ink hover:bg-white/60',
                collapsed && 'justify-center',
              )}>
              <LogOut className="w-4 h-4 flex-shrink-0" />
              {!collapsed && 'Logout'}
            </button>
          </li>
        </ul>
      </div>

      <div
        className="border-t border-shell-border p-3 flex-shrink-0"
        onClick={() => navigate('/settings')}
        onKeyDown={e => e.key === 'Enter' && navigate('/settings')}
        role="button"
        tabIndex={0}>
        <div
          className={cn(
            'flex items-center gap-3 px-2 py-2 rounded-md hover:bg-white/60 cursor-pointer',
            collapsed && 'justify-center',
          )}>
          <div className="w-8 h-8 rounded-full bg-ink flex items-center justify-center text-paper text-sm font-extrabold flex-shrink-0">
            {initial}
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink truncate">{displayName}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-mute" />
            </>
          )}
        </div>
      </div>
    </aside>
  );
};

export default SideBar;
